/* =========================================================================
   ClueVine — Settings context
   Owns theme + accessibility preferences, applies them to <html> as data-
   attributes / CSS variables (which the token layer reacts to), and persists
   them. Resolves "system" theme + system reduced-motion via matchMedia.
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { watchMediaQuery } from '../utils/mediaQuery';
import { loadSlice, saveSlice, type SliceConfig } from './persistence';

export type ThemePref = 'system' | 'light' | 'dark';
export type ThemeSkin = 'default' | 'amber' | 'plum';

export interface Settings {
  theme: ThemePref;
  skin: ThemeSkin;
  sound: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  colorBlind: boolean;
  fontScale: number;
  hintNudges: boolean;
  perfOverlay: boolean;
  haptics: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  skin: 'default',
  sound: true,
  reducedMotion: false,
  highContrast: false,
  colorBlind: false,
  fontScale: 1,
  hintNudges: true,
  perfOverlay: false,
  haptics: true,
};

/** Skins that require the Supporter unlock. */
export const SKIN_LOCKED: Record<ThemeSkin, boolean> = { default: false, amber: true, plum: true };

/** Browser-chrome colour (status bar / address bar) per resolved theme — matches --paper. */
const THEME_COLOR = { light: '#f7f4ec', dark: '#0e1512' } as const;

export const FONT_SCALES = [0.9, 1, 1.15, 1.3] as const;

/**
 * Coerce stored or imported settings into a valid Settings object: unknown keys
 * are dropped, wrong types fall back to defaults, and the text scale is snapped
 * to a supported size (a 0 scale would make every word vanish).
 */
export function normalizeSettings(raw: unknown): Settings {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const bool = (k: keyof Settings) => (typeof r[k] === 'boolean' ? (r[k] as boolean) : (DEFAULT_SETTINGS[k] as boolean));
  const scale = typeof r.fontScale === 'number' ? r.fontScale : DEFAULT_SETTINGS.fontScale;
  const nearest = FONT_SCALES.reduce<number>((a, b) => (Math.abs(b - scale) < Math.abs(a - scale) ? b : a), 1);
  return {
    theme: r.theme === 'light' || r.theme === 'dark' || r.theme === 'system' ? r.theme : DEFAULT_SETTINGS.theme,
    skin: r.skin === 'amber' || r.skin === 'plum' || r.skin === 'default' ? r.skin : DEFAULT_SETTINGS.skin,
    sound: bool('sound'),
    reducedMotion: bool('reducedMotion'),
    highContrast: bool('highContrast'),
    colorBlind: bool('colorBlind'),
    fontScale: nearest,
    hintNudges: bool('hintNudges'),
    perfOverlay: bool('perfOverlay'),
    haptics: bool('haptics'),
  };
}

const slice: SliceConfig<Settings> = {
  name: 'settings',
  version: 1,
  defaults: () => ({ ...DEFAULT_SETTINGS }),
};

interface SettingsContextValue {
  settings: Settings;
  resolvedTheme: 'light' | 'dark';
  effectiveReducedMotion: boolean;
  update: (patch: Partial<Settings>) => void;
  reset: () => void;
  replaceAll: (next: unknown) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

function mq(query: string): MediaQueryList | null {
  return typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null;
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => normalizeSettings(loadSlice(slice)));
  const [systemDark, setSystemDark] = useState(() => mq('(prefers-color-scheme: dark)')?.matches ?? false);
  const [systemReduced, setSystemReduced] = useState(
    () => mq('(prefers-reduced-motion: reduce)')?.matches ?? false,
  );

  useEffect(() => {
    const darkMq = mq('(prefers-color-scheme: dark)');
    const motionMq = mq('(prefers-reduced-motion: reduce)');
    if (!darkMq || !motionMq) return;
    const onDark = () => setSystemDark(darkMq.matches);
    const onMotion = () => setSystemReduced(motionMq.matches);
    const offDark = watchMediaQuery(darkMq, onDark);
    const offMotion = watchMediaQuery(motionMq, onMotion);
    return () => {
      offDark();
      offMotion();
    };
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    settings.theme === 'system' ? (systemDark ? 'dark' : 'light') : settings.theme;
  const effectiveReducedMotion = settings.reducedMotion || systemReduced;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;
    root.dataset.skin = settings.skin;
    root.dataset.contrast = settings.highContrast ? 'high' : 'normal';
    root.dataset.clue = settings.colorBlind ? 'cb' : 'default';
    root.dataset.motion = effectiveReducedMotion ? 'reduced' : 'full';
    root.style.setProperty('--font-scale', String(settings.fontScale));
    // Keep the browser chrome and the next launch's splash in step with the in-app theme.
    for (const meta of Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'))) {
      meta.content = THEME_COLOR[resolvedTheme];
    }
    try {
      window.localStorage.setItem('cluevine:theme-hint', resolvedTheme);
    } catch {
      /* storage unavailable */
    }
    saveSlice(slice, settings);
  }, [settings, resolvedTheme, effectiveReducedMotion]);

  const update = useCallback(
    (patch: Partial<Settings>) => setSettings((s) => ({ ...s, ...patch })),
    [],
  );
  const reset = useCallback(() => setSettings({ ...DEFAULT_SETTINGS }), []);
  const replaceAll = useCallback((next: unknown) => setSettings(normalizeSettings(next)), []);

  const value = useMemo<SettingsContextValue>(
    () => ({ settings, resolvedTheme, effectiveReducedMotion, update, reset, replaceAll }),
    [settings, resolvedTheme, effectiveReducedMotion, update, reset, replaceAll],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within <SettingsProvider>');
  return ctx;
}
